
// @ts-nocheck
/* eslint-disable */
import { defineComponent, computed, onMounted, ref, nextTick, watch } from 'vue'
// import { elFormKey, elFormItemKey } from '@/components/form'
import { isPromise } from '@vue/shared'
import { isBool } from '@/components/utils/util'
import throwError, { warn } from '@/components/utils/error'
import { UPDATE_MODEL_EVENT, CHANGE_EVENT, INPUT_EVENT } from '@/components/utils/constants'

// import type { ElFormContext, ElFormItemContext } from '@/components/form'
import type { PropType } from 'vue'

type ValueType = boolean | string | number;

interface ISwitchProps {
  modelValue: ValueType
  value: ValueType
  disabled: boolean
  width: number
  activeIconClass: string
  inactiveIconClass: string
  activeText: string
  inactiveText: string
  activeColor: string
  inactiveColor: string
  activeValue: ValueType
  inactiveValue: ValueType
  name: string
  validateEvent: boolean
  id: string
  loading:boolean
  beforeChange?: () => (Promise<boolean> | boolean)
}

export default defineComponent({
  name: 'MsSwitch',

  emits: [UPDATE_MODEL_EVENT, CHANGE_EVENT, INPUT_EVENT],

  props: {
    modelValue: {
      type: [Boolean, String, Number],
      default: false
    },
    value: {
      type: [Boolean, String, Number],
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    width: {
      type: Number,
      default: 40
    },
    activeIconClass: {
      type: String,
      default: ''
    },
    inactiveIconClass: {
      type: String,
      default: ''
    },
    activeText: {
      type: String,
      default: ''
    },
    inactiveText: {
      type: String,
      default: ''
    },
    activeColor: {
      type: String,
      default: ''
    },
    inactiveColor: {
      type: String,
      default: ''
    },
    activeValue: {
      type: [Boolean, String, Number],
      default: true
    },
    inactiveValue: {
      type: [Boolean, String, Number],
      default: false
    },
    name: {
      type: String,
      default: ''
    },
    validateEvent: {
      type: Boolean,
      default: true
    },
    id: String,
    loading: {
      type: Boolean,
      default: false
    },
    beforeChange: Function as PropType<() => (Promise<boolean> | boolean)>
  },
  setup (props: ISwitchProps, ctx) {
    // const elForm = inject(elFormKey, {} as ElFormContext)
    // const elFormItem = inject(elFormItemKey, {} as ElFormItemContext)

    const isModelValue = ref(props.modelValue !== false)
    const input = ref(null)
    const core = ref(null)

    const scope = 'MsSwitch'

    watch(() => props.modelValue, () => {
      isModelValue.value = true
    })

    watch(() => props.value, () => {
      isModelValue.value = false
    })

    const actualValue = computed((): ValueType => {
      return isModelValue.value ? props.modelValue : props.value
    })

    const checked = computed((): boolean => {
      return actualValue.value === props.activeValue
    })

    if (!~[props.activeValue, props.inactiveValue].indexOf(actualValue.value)) {
      ctx.emit(UPDATE_MODEL_EVENT, props.inactiveValue)
      ctx.emit(CHANGE_EVENT, props.inactiveValue)
      ctx.emit(INPUT_EVENT, props.inactiveValue)
    }

    watch(checked, () => {
      input.value.checked = checked.value

      if (props.activeColor || props.inactiveColor) {
        setBackgroundColor()
      }

      // if (props.validateEvent) {
      //   elFormItem.formItemMitt?.emit('el.form.change', [actualValue.value])
      // }
    })

    const switchDisabled = computed((): boolean => {
      return props.disabled || props.loading
    })

    const handleChange = (): void => {
      const val = checked.value ? props.inactiveValue : props.activeValue
      ctx.emit(UPDATE_MODEL_EVENT, val)
      ctx.emit(CHANGE_EVENT, val)
      ctx.emit(INPUT_EVENT, val)
      nextTick(() => {
        input.value.checked = checked.value
      })
    }

    const switchValue = (): void => {
      if (switchDisabled.value) return

      const { beforeChange } = props
      if (!beforeChange) {
        handleChange()
        return
      }

      const shouldChange = beforeChange()

      const isExpectType = [isPromise(shouldChange), isBool(shouldChange)].some(i => i)
      if (!isExpectType) {
        throwError(scope, 'beforeChange must return type `Promise<boolean>` or `boolean`')
      }

      if (isPromise(shouldChange)) {
        shouldChange.then(result => {
          if (result) {
            handleChange()
          }
        }).catch(e => {
          if (process.env.NODE_ENV !== 'production') {
            warn(scope, `some error occurred: ${e}`)
          }
        })
      } else if (shouldChange) {
        handleChange()
      }
    }

    const setBackgroundColor = (): void => {
      const newColor = checked.value ? props.activeColor : props.inactiveColor
      const coreEl = core.value
      coreEl.style.borderColor = newColor
      coreEl.style.backgroundColor = newColor
      coreEl.children[0].style.color = newColor
    }

    const focus = (): void => {
      input.value?.focus?.()
    }

    onMounted(() => {
      if (props.activeColor || props.inactiveColor) {
        setBackgroundColor()
      }

      input.value.checked = checked.value
    })

    return {
      input,
      core,
      switchDisabled,
      checked,
      handleChange,
      switchValue,
      focus
    }
  }
})
